import type { Maybe } from '@adornis/base/utilTypes.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { TextareaInput } from '@adornis/formfield/fields/textarea-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { Collapse } from '@adornis/components/client/Collapse.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { differenceInMonths } from 'date-fns';
import { html, nothing } from 'lit';
import { Anstellungsform, PauschalbesteuerungMiniJob } from '../../../../db/enums/misc-enums.js';
import { calculateEndOfTrialTime } from '../../../api/calculateEndOfTrialTime.js';
import { getHistoryForPath } from '../../../api/getHistoryForPath.js';
import { getReadableHistoryStrings } from '../../../api/getReadableHistoryStrings.js';
import type { AppliedContract } from '../../../entities/AppliedContract.js';
import { BesonderePersonengruppe } from '../../../enums/BesonderePersonengruppe.js';
import { Probezeit } from '../../../enums/Probezeit.js';
import type { ContractForm } from './sharedContractEditParts.js';

const TrialTimeDiffText = (contract: Maybe<AppliedContract>) => {
  if (!contract) return nothing;

  const trialEnd = calculateEndOfTrialTime(contract);
  if (!trialEnd) return nothing;

  const monthDiff = differenceInMonths(trialEnd, new Date());
  const relativeString = monthDiff < 0 ? `vor ${monthDiff * -1} Monaten` : `in ${monthDiff} Monaten`;

  return html`<p>Am ${formatCalendarDay(trialEnd, 'dd.MM.yyyy')}, ${relativeString}</p>`;
};

interface ContractEditGeneralSectionProps {
  form: ContractForm;
  contract: Maybe<AppliedContract>;
  onKuendigungSet: (date: Date) => void;
  onChanged: () => void;
}

/** Allgemeine Informationen of the expert contract edit */
export const ContractEditGeneralSection = stateful((props: ContractEditGeneralSectionProps) => {
  const historyFor = (path: string) => getReadableHistoryStrings(getHistoryForPath(props.contract?.history, path));

  return html`
    <div class="flex flex-col gap-3">
      ${CardHeader({ title: 'Allgemeine Informationen' })}
      <div class="grid grid-cols-1 md:grid-cols-[1fr_2fr_1fr_2fr] items-center gap-2">
        ${FormGridEntry({
          label: 'Einstellungsdatum',
          render: DateInput({
            ...props.form.field('agInfos', 'einstellungsdatum'),
            clearable: true,
            placeholder: 'Wann erfolgt die Einstellung?',
            placeholderMode: 'hide',
          }),
          history: historyFor('agInfos.einstellungsdatum'),
        })}
        ${FormGridEntry({
          label: 'Unterzeichnungsdatum',
          render: DateInput({
            ...props.form.field('agInfos', 'unterzeichnungsdatum'),
            clearable: true,
            placeholder: 'Wann wurde der Vertrag vom Arbeitnehmer unterzeichnet?',
            placeholderMode: 'hide',
          }),
          history: historyFor('agInfos.unterzeichnungsdatum'),
        })}
        ${FormGridEntry({
          label: 'Beendet zum',
          render: DateInput({
            ...props.form.field('agInfos', 'gekuendigtZum'),
            clearable: true,
            placeholder: 'Wann wurde der AV beendet? (Gekündigt, Rente, ...)',
            placeholderMode: 'hide',
            onPicked: (date: Date | null) => {
              props.form.field('agInfos', 'gekuendigtZum').onPicked?.(date);
              if (date) props.onKuendigungSet(date);
            },
          }),
          history: historyFor('agInfos.gekuendigtZum'),
        })}

        <hr class="col-span-full border-base-300" />

        <div class="col-span-full">
          ${Collapse({
            className: 'border-0 bg-transparent shadow-none',
            heading: html`<span class="text-base font-semibold">Weitere Informationen</span>`,
            content: html`
              <div class="grid grid-cols-1 md:grid-cols-[1fr_2fr_1fr_2fr] items-center gap-2">
                <div class="col-span-full grid grid-cols-[1fr_5fr] items-center gap-2">
                  ${FormGridEntry({
                    label: 'Nebenabrede',
                    render: TextareaInput({
                      ...props.form.field('agInfos', 'nebenabrede'),
                      placeholder: 'Nebenabrede',
                      rows: 4,
                    }),
                    history: historyFor('agInfos.nebenabrede'),
                  })}
                </div>
                ${FormGridEntry({
                  label: 'Dokumente vorgelegt?',
                  render: ToggleInput({
                    ...props.form.field('agInfos', 'alleDokumenteVorgelegt'),
                    label: props.form.document?.agInfos?.alleDokumenteVorgelegt ? 'Ja' : 'Nein',
                  }),
                  history: historyFor('agInfos.alleDokumenteVorgelegt'),
                })}
                ${FormGridEntry({
                  label: 'Urlaubstage',
                  labelAddition: html`<span class="text-sm text-base-content/70"> 20 + </span>`,
                  render: html`
                    <div class="flex flex-row items-center gap-2">
                      ${NumberInput({
                        ...props.form.field('agInfos', 'urlaubsTage'),
                        placeholder: 'Urlaubstage',
                        placeholderMode: 'hide',
                        step: 0.5,
                        min: 0,
                        onPicked: (value: number | null) => {
                          props.form.field('agInfos', 'urlaubsTage').onPicked?.(value);
                          props.onChanged();
                        },
                      })}
                      <span class="whitespace-nowrap">
                        = ${20 + (props.form.document?.agInfos?.urlaubsTage ?? 0)}
                      </span>
                    </div>
                  `,
                  history: historyFor('agInfos.urlaubsTage'),
                })}
                ${FormGridEntry({
                  label: 'Anstellungsform',
                  render: DropdownSelectionInput({
                    ...props.form.field('agInfos', 'anstellungsform'),
                    selectables: Object.values(Anstellungsform),
                    placeholder: 'Keine Anstellungsform',
                    clearable: true,
                  }),
                  history: historyFor('agInfos.anstellungsform'),
                })}
                ${FormGridEntry({
                  label: 'Pauschalbesteuerung bei geringfügiger Beschäftigung',
                  render: DropdownSelectionInput({
                    ...props.form.field('agInfos', 'pauschalbesteuerungMiniJob'),
                    selectables: Object.values(PauschalbesteuerungMiniJob),
                    placeholder: 'Pauschalbesteuerung',
                    clearable: true,
                  }),
                  history: historyFor('agInfos.pauschalbesteuerungMiniJob'),
                })}
                ${FormGridEntry({
                  label: 'Besondere Personengruppe',
                  render: DropdownSelectionInput({
                    ...props.form.field('agInfos', 'besonderePersonengruppe'),
                    selectables: Object.values(BesonderePersonengruppe),
                    placeholder: 'Keine besondere Personengruppe',
                    clearable: true,
                  }),
                  history: historyFor('agInfos.besonderePersonengruppe'),
                })}
                ${FormGridEntry({
                  label: 'Gestellungsvertrag',
                  render: ToggleInput({
                    ...props.form.field('agInfos', 'isGestellungsContract'),
                    label: props.form.document?.agInfos?.isGestellungsContract ? 'Ja' : 'Nein',
                  }),
                  history: historyFor('agInfos.isGestellungsContract'),
                })}
                ${FormGridEntry({
                  label: 'Werkstudent geprüft',
                  render: ToggleInput({
                    ...props.form.field('agInfos', 'workingStudentChecked'),
                    label: props.form.document?.agInfos?.workingStudentChecked ? 'Ja' : 'Nein',
                  }),
                  history: historyFor('agInfos.workingStudentChecked'),
                })}

                <hr class="col-span-full border-base-300" />

                ${FormGridEntry({
                  label: 'Probezeit',
                  render: DropdownSelectionInput({
                    ...props.form.field('agInfos', 'dauerProbezeit'),
                    selectables: Object.values(Probezeit),
                    placeholder: 'Dauer der Probezeit',
                    clearable: true,
                    onPicked: (value: Probezeit | null) => {
                      props.form.field('agInfos', 'dauerProbezeit').onPicked?.(value);
                      props.onChanged();
                    },
                  }),
                  history: historyFor('agInfos.dauerProbezeit'),
                })}
                ${
                  props.form.document?.agInfos?.dauerProbezeit
                    ? FormGridEntry({ label: 'Ende der Probezeit', render: TrialTimeDiffText(props.contract) })
                    : nothing
                }
              </div>
            `,
            defaultOpen: false,
          })}
        </div>
      </div>
    </div>
  `;
});
